import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);

  useEffect(() => {
    const savedCart = JSON.parse(localStorage.getItem('cartdata')) || [];
    const savedWishlist = JSON.parse(localStorage.getItem('wishlist')) || [];
    setCartItems(savedCart);
    setWishlistItems(savedWishlist);
  }, []);

  const saveCart = (items) => {
    localStorage.setItem('cartdata', JSON.stringify(items));
    setCartItems(items);
  };

  const saveWishlist = (items) => {
    localStorage.setItem('wishlist', JSON.stringify(items));
    setWishlistItems(items);
  };

  const addToCart = (product, quantity = 1) => {
    const userId = localStorage.getItem('UserId');
    if (!userId) {
      return { success: false, needsLogin: true };
    }

    const existingItem = cartItems.find((item) => item._id === product._id);

    if (existingItem) {
      toast.success('Item already in cart!');
      return { success: false, message: 'Already in cart' };
    }

    const newItem = {
      UserId: userId,
      ProductName: product.ProductName,
      ProductMRP: product.ProductMRP,
      ProductPrice: product.ProductPrice,
      ProductColor: product.ProductColor,
      ProductSize: product.ProductSize || product.selectedVariant || '250g',
      ProductQuantity: quantity,
      VendorId: product.VendorId,
      ProductMainImgUrl: product.ProductMainImgUrl,
      ProductGST: product.ProductGST,
      ProductDiscount: product.ProductDiscount,
      ProductHSN: product.ProductHSN,
      _id: product._id,
    };

    const updatedCart = [...cartItems, newItem];
    saveCart(updatedCart);
    toast.success('Added to cart!');
    return { success: true };
  };

  const removeFromCart = (productId) => {
    const updatedCart = cartItems.filter((item) => item._id !== productId);
    saveCart(updatedCart);
    toast.success('Removed from cart');
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }

    const updatedCart = cartItems.map((item) =>
      item._id === productId ? { ...item, ProductQuantity: newQuantity } : item
    );
    saveCart(updatedCart);
  };

  const clearCart = () => {
    localStorage.removeItem('cartdata');
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      return total + item.ProductPrice * item.ProductQuantity;
    }, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.ProductQuantity, 0);
  };

  // Wishlist functions
  const addToWishlist = (product) => {
    const userId = localStorage.getItem('UserId');
    if (!userId) {
      return { success: false, needsLogin: true };
    }

    const exists = wishlistItems.find((item) => item._id === product._id);
    if (exists) {
      toast.info('Already in wishlist!');
      return { success: false };
    }

    const updatedWishlist = [...wishlistItems, product];
    saveWishlist(updatedWishlist);
    toast.success('Added to wishlist!');
    return { success: true };
  };

  const removeFromWishlist = (productId) => {
    const updatedWishlist = wishlistItems.filter((item) => item._id !== productId);
    saveWishlist(updatedWishlist);
    toast.success('Removed from wishlist');
  };

  const isInWishlist = (productId) => {
    return wishlistItems.some((item) => item._id === productId);
  };

  const isInCart = (productId) => {
    return cartItems.some((item) => item._id === productId);
  };

  const moveToCartFromWishlist = (product) => {
    const result = addToCart(product);
    if (result.success) {
      removeFromWishlist(product._id);
    }
    return result;
  };

  const value = {
    cartItems,
    wishlistItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    isInCart,
    moveToCartFromWishlist,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
